import { MappedEventTypes } from '@src/common/types/empTypes';
import { IMappedEvent } from '@src/pages/metric-templates/types';
import { isEmptyOrNotEmptyOp } from '@src/emp-3.0/utils/checks';
import { validateString } from '@src/emp-3.0/utils';
import { EStringOp } from '@src/emp-3.0/types';
import { MATCH_PATTERN_ERROR_MESSAGE } from '@src/emp-3.0/constants/common';

// Check metadata rules
const checkMetadataRules = (rules: []) => {
  const result = {
    failed: false,
    errorFields: [] as ('empty_metadata_key_value' | 'match_pattern_error')[],
  };

  rules.forEach((event) => {
    if (event.metadata && event.metadata.length > 0) {
      event.metadata.forEach((metadataRules) => {
        metadataRules.forEach((rule) => {
          if (!rule.key || !rule.value?.[0]) {
            result.failed = true;
            result.errorFields.push('empty_metadata_key_value');
          }

          const { failed } = validateString(rule.value[0], {
            matchPattern: rule.op === EStringOp.MATCH_PATTERN,
          });
          if (failed) {
            result.failed = true;
            result.errorFields.push('match_pattern_error');
          }
        });
      });
    }
  });
  return result;
};

// Universal function: traverses all rules
const checkAllRules = (rules: any[], callback: (rule: any) => boolean): boolean => {
  return rules.every((andRules) =>
    andRules.every((orRule) =>
      orRule.mapping_rules.every((andMappingRules) =>
        andMappingRules.every((orMappingRule) => callback(orMappingRule)),
      ),
    ),
  );
};

type ValidateResult = {
  key?: string[];
  result?: string;
  status: boolean;
  mappedEvent?: any;
  fields: string[];
  // for example, { 0: [0, 1], 1: [2] } means that the first rule has errors in the first and second items, and the second rule has an error in the third item.
  errorIndex: Record<number, number[]>;
};

export const validateMappedEventData = (
  mappedEvent: MappedEventTypes | IMappedEvent,
  needValidateEventName: boolean = true,
): ValidateResult => {
  //validate mapped eventname and event_category
  if (!mappedEvent.event_name) {
    return {
      key: ['Mapped event name'],
      result: 'is null',
      status: false,
      fields: needValidateEventName ? ['mapped_event_name'] : [],
      errorIndex: {},
    };
  }
  //check mapped events
  let eventRes: ValidateResult = {
    key: ['event name'],
    result: 'is null',
    status: false,
    fields: [],
    errorIndex: {},
  };
  if (needValidateEventName) {
    eventRes = {
      key: ['Select at least one raw event to proceed.'],
      result: '',
      status: false,
      fields: ['raw_event_name'],
      errorIndex: {},
    };
  }
  const eventStatus = mappedEvent.rules.every((andRules, andIndex) => {
    return (andRules ?? []).every((orRule, index) => {
      // Check that raw_event_name contains special characters.
      const { failed, message } = validateString(orRule?.value?.[0], {
        validateLegality: false,
        validateSecure: true,
      });
      if (failed) {
        eventRes.key = ['[raw event]:'];
        eventRes.result = message;
        eventRes.errorIndex[index] = [0];
      }
      if (!orRule?.value?.length) {
        eventRes.key = ['[raw event]:'];
        eventRes.result = 'is null';
        eventRes.fields = ['raw_event_name'];
        eventRes.errorIndex[index] = [0];
      }

      return orRule?.value?.length && orRule?.op && !failed;
    });
  });
  if (!eventStatus) return eventRes;

  // remove rules without tag_key and tag_value
  mappedEvent.rules.forEach((andRules) => {
    andRules.forEach((orRule) => {
      orRule.mapping_rules = orRule.mapping_rules.filter((andMappingRules) => {
        return andMappingRules.filter((orMappingRule) => {
          return (
            orMappingRule.tag_key.value.length ||
            orMappingRule.tag_value.value.length ||
            isEmptyOrNotEmptyOp(orMappingRule.tag_value.op)
          );
        }).length;
      });
      // remove metadata rules without key
      orRule.metadata = orRule.metadata.filter((andMetadataRules) => {
        return andMetadataRules.filter((orMetadataRule) => {
          return !!orMetadataRule.key;
        }).length;
      });
    });
  });

  // Checks if tag_key and tag_value satisfy specified conditions.
  const eventRuleRes = {
    key: [] as string[],
    result: 'is null',
    status: false,
    fields: ['tag_key_value'],
    errorIndex: {},
  };
  const checkEventRules = (mappedEvent: any) => {
    return checkAllRules(mappedEvent.rules, (rule) => {
      const hasKey = rule.tag_key.op && rule.tag_key.value.length > 0;
      const hasValue = rule.tag_value.op && rule.tag_value.value.length > 0;
      const { failed: isIllegalityTagKey, message: validateTagKeyMessage } = validateString(
        rule.tag_key.value?.[0],
      );
      const { failed: isllegalityTagValue, message: validateTagValueMessage } = validateString(
        rule.tag_value.value?.[0],
        {
          matchPattern: rule.tag_value.op === EStringOp.MATCH_PATTERN,
          validateLegality: false,
          validateSecure: true,
        },
      );

      // Check that tag_key contains special characters.
      if ((hasKey && isIllegalityTagKey) || (hasValue && isllegalityTagValue)) {
        if (isIllegalityTagKey) {
          eventRuleRes.key = ['[tag key]:'];
        }
        if (isllegalityTagValue) {
          eventRuleRes.key = ['[tag value]:'];
        }
        if (isIllegalityTagKey && isllegalityTagValue) {
          eventRuleRes.key = ['[tag key OR tag value]:'];
        }
        if (isllegalityTagValue && rule.tag_value.op === EStringOp.MATCH_PATTERN) {
          eventRuleRes.key = ['[tag value]:'];
          eventRuleRes.fields = ['match_pattern_error'];
        }

        if (isIllegalityTagKey) {
          eventRuleRes.result = `${validateTagKeyMessage}`;
        } else {
          eventRuleRes.result = `${validateTagValueMessage}`;
        }
        return !isIllegalityTagKey && !isllegalityTagValue;
      } else if (isEmptyOrNotEmptyOp(rule.tag_value.op)) {
        if (!hasKey && eventRuleRes.key.length === 0) eventRuleRes.key = ['tag key'];
        eventRuleRes.result = 'is null';
        return hasKey;
      } else {
        if (!(hasKey && hasValue) && eventRuleRes.key.length === 0)
          eventRuleRes.key = ['tag key', 'tag value'];
        eventRuleRes.result = 'is null';
        return hasKey && hasValue;
      }
    });
  };

  const mappedEventCheckFailed = !checkEventRules(mappedEvent);

  const { failed: metaDataCheckFailed, errorFields } = checkMetadataRules(mappedEvent.rules[0]);

  if (mappedEventCheckFailed) {
    return eventRuleRes;
  }
  if (metaDataCheckFailed) {
    if (errorFields.includes('empty_metadata_key_value')) {
      return {
        result: 'The metadata key and value cannot be empty.',
        key: ['value'] as string[],
        status: false,
        fields: ['metadata_key_value'],
        errorIndex: {},
      };
    } else if (errorFields.includes('match_pattern_error')) {
      return {
        result: MATCH_PATTERN_ERROR_MESSAGE,
        key: ['[Metadata]'] as string[],
        status: false,
        fields: ['match_pattern_error'],
        errorIndex: {},
      };
    }
  }

  return { mappedEvent: mappedEvent, status: true, fields: [], errorIndex: {} };
};
